import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { getRecordArray, getString } from "@/lib/pageContent";

interface FAQSectionProps {
  content?: Record<string, unknown>;
}

const fallbackFaqs = [
  {
    q: "What is MVIBE and what services do you offer?",
    a: "MVIBE is a transformative coaching, training, and image consulting firm founded by Mahirah (Sana Amrin). We offer Soft Skills and Behavioural Training for Corporates, Campus-to-Corporate Training for Students, Life Coaching for Working Professionals, and Personal Branding through Image Consulting.",
  },
  {
    q: "Who is the founder of MVIBE and what are her qualifications?",
    a: "Mahirah (Sana Amrin) is an Executive Facilitator, Soft Skills Trainer, and ICF-aligned Life Coach with 15+ years of corporate experience in IT HR leadership and operations.",
  },
  {
    q: "Who are MVIBE's training programmes designed for?",
    a: "Our programmes serve CXOs, DGMs, and senior leadership teams; final-year students and graduate trainees; working professionals, managers, and entrepreneurs across India and the UAE.",
  },
  {
    q: "What topics does MVIBE cover in corporate soft skills training?",
    a: "We cover Executive Presence and Leadership Impact, Strategic Communication and Influence, Decision-Making Under Pressure, Emotional Intelligence for Senior Leaders, Stakeholder Management and Difficult Conversations, Leading Through Change and Burnout, and Systems Thinking.",
  },
  {
    q: "How can I book a discovery call with MVIBE?",
    a: "You can book a complimentary discovery call through our Contact page, or reach us directly via WhatsApp, email, or LinkedIn.",
  },
];

const FAQSection = ({ content }: FAQSectionProps) => {
  const [open, setOpen] = useState<number | null>(null);
  const title = getString(content?.title, "Frequently Asked Questions");
  const ctaLabel = getString(content?.ctaLabel, "See More FAQs →");
  const faqs = getRecordArray<Record<string, unknown>>(content?.items, fallbackFaqs).map((item, index) => ({
    q: getString(item.q, fallbackFaqs[index]?.q ?? ""),
    a: getString(item.a, fallbackFaqs[index]?.a ?? ""),
  }));

  return (
    <section className="bg-primary py-10">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-5xl items-start gap-4 md:grid-cols-2">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6 text-3xl font-heading font-bold text-primary-foreground md:text-5xl"
            >
              {title}
            </motion.h2>
            <div className="space-y-2.5">
              {faqs.map((faq, i) => (
                <motion.div
                  key={faq.q}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="overflow-hidden rounded-lg border border-primary-foreground/10"
                >
                  <button
                    onClick={() => setOpen(open === i ? null : i)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left font-sans text-base font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/5"
                  >
                    {faq.q}
                    <ChevronDown className={`ml-2 h-4 w-4 shrink-0 text-accent transition-transform duration-200 ${open === i ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${open === i ? "max-h-96" : "max-h-0"}`}>
                    <p className="px-4 pb-3 font-sans text-base leading-relaxed text-primary-foreground/70">{faq.a}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="hidden md:block"
          >
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600"
              alt="Team coaching session"
              className="max-h-[450px] h-full w-full rounded-xl object-cover"
              loading="lazy"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
